import { Component, DestroyRef, inject, input, output } from '@angular/core';
import { CustomFilterModel, PageConfigName } from '@bo-client/shared/models/userBackOfficeSettings';
import { UserConfigService } from '@bo-client/shared/services/user-config.service';
import { BehaviorSubject, Observable, switchMap, combineLatest } from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { map, shareReplay, tap } from 'rxjs/operators';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { CustomFilterModalComponent } from '@bo-client/shared/entity-list-page/custom-filter/custom-filter-modal/custom-filter-modal.component';
import { MatDialogSize } from '@bo-client/shared/constants/mat-dialog-size';
import { GenericTwoOptionsModalComponent } from '@bo-client/shared/modals/generic-two-options-modal/generic-two-options-modal.component';

@Component({
  selector: 'custom-filter-menu',
  templateUrl: './custom-filter-menu.component.html',
  styleUrls: ['./custom-filter-menu.component.scss']
})
export class CustomFilterMenuComponent {
  private userConfigService = inject(UserConfigService);
  private matDialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  currentConfig = input.required<CustomFilterModel['config']>();
  pageId = input.required<PageConfigName>();

  onConfigApply = output<CustomFilterModel['config']>();

  private readonly _refresh$ = new BehaviorSubject<void>(undefined);

  isLoading = true;

  readonly filterList$: Observable<CustomFilterModel[]> = combineLatest([
    this._refresh$.asObservable(),
    toObservable(this.pageId)
  ]).pipe(
    takeUntilDestroyed(),
    map(([, pageId]) => pageId),
    switchMap((pageId) => this.userConfigService.pageConfig.get$(pageId)),
    map((pageConfig) => {
      const filters = pageConfig?.customFilters ?? [];
      return filters.slice().sort((a, b) => a.id - b.id);
    }),
    tap(() => (this.isLoading = false)),
    shareReplay(1)
  );

  applyConfig = (config: CustomFilterModel['config']) => {
    this.onConfigApply.emit(config);
  };

  saveFilter = (customFilter: CustomFilterModel, filterList: CustomFilterModel[]) => {
    this.isLoading = true;
    const filterIndex = filterList.findIndex((filter) => filter.id === customFilter.id);
    if (filterIndex !== -1) {
      filterList[filterIndex] = customFilter;
    } else {
      filterList.push(customFilter);
    }
    this.userConfigService.pageConfig.update({ customFilters: filterList }, this.pageId(), () =>
      this._refresh$.next()
    );
  };

  openEdit(filterItem: CustomFilterModel, filterList: CustomFilterModel[]) {
    const dialogConfig = new MatDialogConfig<{ name: CustomFilterModel['name'] }>();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'custom-filter-edit-modal';
    dialogConfig.maxHeight = '70%';
    dialogConfig.width = '70%';
    dialogConfig.data = {
      name: filterItem.name
    };
    this.matDialog
      .open<CustomFilterModalComponent, { name: CustomFilterModel['name'] }, string | null>(
        CustomFilterModalComponent,
        dialogConfig
      )
      .afterClosed()
      .subscribe((name) => {
        if (name) {
          this.saveFilter({ ...filterItem, name }, filterList);
        }
      });
  }

  openDelete(filterItem: CustomFilterModel, filterList: CustomFilterModel[]) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'custom-filter-delete-modal';
    dialogConfig.maxHeight = MatDialogSize.maxHeight;
    dialogConfig.width = MatDialogSize.width;
    dialogConfig.data = {
      title: 'Konfiguration löschen',
      message: `Sind Sie sicher, dass Sie die Konfiguration ${filterItem.name} löschen möchten?`,
      optionA: 'Abbrechen',
      optionB: 'Löschen bestätigen'
    };
    this.matDialog
      .open<
        GenericTwoOptionsModalComponent,
        {
          title: string;
          message: string;
          optionA: string;
          optionB: string;
        },
        'optionA' | 'optionB'
      >(GenericTwoOptionsModalComponent, dialogConfig)
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result === 'optionB') {
          const updatedList = filterList.filter((filter) => filter.id !== filterItem.id);
          this.userConfigService.pageConfig.update({ customFilters: updatedList }, this.pageId(), () =>
            this._refresh$.next()
          );
        }
      });
  }

  openNew(currentConfig: CustomFilterModel['config'], filterList: CustomFilterModel[]) {
    const highestId = filterList.reduce((maxId, filter) => Math.max(maxId, filter.id), 0);
    const newFilter: CustomFilterModel = {
      id: highestId + 1,
      name: `Neue Konfiguration ${highestId + 1}`,
      config: currentConfig
    };
    this.openEdit(newFilter, filterList);
  }
}
